/**
 * IntegrationsPage Component
 *
 * Main page for managing integrations with:
 * - Tabbed navigation between integration types
 * - Connection lists and forms
 * - Notification channel management
 */
import React, { useState } from 'react';
import {
  Tab,
  TabList,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  TableHeaderCell,
  TableCell,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  AlertRegular,
  CodeRegular,
  BoardRegular,
  CloudRegular,
  BranchRegular,
  ShieldCheckmarkRegular,
  RocketRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { IntegrationConnectionsList, type ConnectionItem } from './IntegrationConnectionsList';
import { GitHubConnectionForm } from './GitHubConnectionForm';
import { GitLabConnectionForm } from './GitLabConnectionForm';
import { JiraConnectionForm } from './JiraConnectionForm';
import { AzureDevOpsConnectionForm } from './AzureDevOpsConnectionForm';
import { ServiceNowConnectionForm } from './ServiceNowConnectionForm';
import { SnykConnectionForm } from './SnykConnectionForm';
import { NotificationChannelsList } from './NotificationChannelsList';
import { NotificationChannelForm } from './NotificationChannelForm';
import { IntegrationSetupGuide } from './IntegrationSetupGuide';
import { integrationSetupData } from '../constants/setupInstructions';
import {
  useGitHubConnections,
  useTestGitHubConnection,
  useDeleteGitHubConnection,
  useGitLabConnections,
  useTestGitLabConnection,
  useDeleteGitLabConnection,
  useAzureDevOpsConnections,
  useTestAzureDevOpsConnection,
  useDeleteAzureDevOpsConnection,
  useJiraConnections,
  useTestJiraConnection,
  useDeleteJiraConnection,
  useServiceNowConnections,
  useTestServiceNowConnection,
  useDeleteServiceNowConnection,
  useSnykConnections,
  useTestSnykConnection,
  useDeleteSnykConnection,
} from '../hooks';
import type { GitHubConnection, GitLabConnection, AzureDevOpsConnection, JiraConnection, ServiceNowConnection, SnykConnection } from '../types';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  tabContent: {
    marginTop: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  dialogContent: {
    minWidth: '500px',
  },
});

type TabValue = 'github' | 'gitlab' | 'azure-devops' | 'jira' | 'servicenow' | 'snyk' | 'notifications';

interface FormDialogState {
  isOpen: boolean;
  type: TabValue | null;
}

export const IntegrationsPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabValue>('github');
  const [formDialog, setFormDialog] = useState<FormDialogState>({ isOpen: false, type: null });

  // GitHub hooks
  const gitHubConnections = useGitHubConnections();
  const testGitHub = useTestGitHubConnection();
  const deleteGitHub = useDeleteGitHubConnection();

  // GitLab hooks
  const gitLabConnections = useGitLabConnections();
  const testGitLab = useTestGitLabConnection();
  const deleteGitLab = useDeleteGitLabConnection();

  // Azure DevOps hooks
  const azureDevOpsConnections = useAzureDevOpsConnections();
  const testAzureDevOps = useTestAzureDevOpsConnection();
  const deleteAzureDevOps = useDeleteAzureDevOpsConnection();

  // Jira hooks
  const jiraConnections = useJiraConnections();
  const testJira = useTestJiraConnection();
  const deleteJira = useDeleteJiraConnection();

  // ServiceNow hooks
  const serviceNowConnections = useServiceNowConnections();
  const testServiceNow = useTestServiceNowConnection();
  const deleteServiceNow = useDeleteServiceNowConnection();

  // Snyk hooks
  const snykConnections = useSnykConnections();
  const testSnyk = useTestSnykConnection();
  const deleteSnyk = useDeleteSnykConnection();

  const hasRepoConnections = (gitHubConnections.data?.length ?? 0) + (gitLabConnections.data?.length ?? 0) + (azureDevOpsConnections.data?.length ?? 0) > 0;

  const [testingConnectionId, setTestingConnectionId] = useState<number | undefined>();

  const handleOpenForm = (type: TabValue) => {
    setFormDialog({ isOpen: true, type });
  };

  const handleCloseForm = () => {
    setFormDialog({ isOpen: false, type: null });
  };

  const handleFormSuccess = () => {
    handleCloseForm();
  };

  const handleTestGitHubConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testGitHub.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const handleTestGitLabConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testGitLab.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const handleTestJiraConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testJira.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const handleTestAzureDevOpsConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testAzureDevOps.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const handleTestServiceNowConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testServiceNow.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const handleTestSnykConnection = async (connectionId: number) => {
    setTestingConnectionId(connectionId);
    try {
      return await testSnyk.mutateAsync(connectionId);
    } finally {
      setTestingConnectionId(undefined);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'github':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<GitHubConnection & ConnectionItem>
              title="GitHub Connections"
              icon={<BranchRegular />}
              connections={gitHubConnections.data?.map(c => ({ ...c, instanceUrl: `github.com/${c.organizationOrUser}` })) || []}
              isLoading={gitHubConnections.isLoading}
              error={gitHubConnections.error}
              isTestingConnection={testGitHub.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteGitHub.isPending}
              onTestConnection={handleTestGitHubConnection}
              onDeleteConnection={(id) => deleteGitHub.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('github')}
              emptyStateText="No GitHub connections configured. Add a connection to scan your repositories."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.github} />}
              customHeaders={
                <>
                  <TableHeaderCell>Repositories</TableHeaderCell>
                  <TableHeaderCell>Auto-Scan PRs</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.repositoryCount}</TableCell>
                  <TableCell>{connection.autoScanPullRequests ? '\u2713 Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'gitlab':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<GitLabConnection & ConnectionItem>
              title="GitLab Connections"
              icon={<CodeRegular />}
              connections={gitLabConnections.data?.map(c => ({ ...c, instanceUrl: c.instanceUrl })) || []}
              isLoading={gitLabConnections.isLoading}
              error={gitLabConnections.error}
              isTestingConnection={testGitLab.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteGitLab.isPending}
              onTestConnection={handleTestGitLabConnection}
              onDeleteConnection={(id) => deleteGitLab.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('gitlab')}
              emptyStateText="No GitLab connections configured. Add a connection to scan your repositories."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.gitlab} />}
              customHeaders={
                <>
                  <TableHeaderCell>Webhooks</TableHeaderCell>
                  <TableHeaderCell>Auto-Scan MRs</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.webhooksEnabled ? '✓ Enabled' : 'Disabled'}</TableCell>
                  <TableCell>{connection.autoScanMergeRequests ? '✓ Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'azure-devops':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<AzureDevOpsConnection & ConnectionItem>
              title="Azure DevOps Connections"
              icon={<CloudRegular />}
              connections={azureDevOpsConnections.data?.map(c => ({ ...c, instanceUrl: c.organizationUrl })) || []}
              isLoading={azureDevOpsConnections.isLoading}
              error={azureDevOpsConnections.error}
              isTestingConnection={testAzureDevOps.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteAzureDevOps.isPending}
              onTestConnection={handleTestAzureDevOpsConnection}
              onDeleteConnection={(id) => deleteAzureDevOps.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('azure-devops')}
              emptyStateText="No Azure DevOps connections configured. Add a connection to scan your repositories."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData['azure-devops']} />}
              customHeaders={
                <>
                  <TableHeaderCell>Webhooks</TableHeaderCell>
                  <TableHeaderCell>Auto-Scan PRs</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.webhooksEnabled ? '✓ Enabled' : 'Disabled'}</TableCell>
                  <TableCell>{connection.autoScanPullRequests ? '✓ Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'jira':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<JiraConnection & ConnectionItem>
              title="Jira Connections"
              icon={<BoardRegular />}
              connections={jiraConnections.data?.map(c => ({ ...c, instanceUrl: c.instanceUrl })) || []}
              isLoading={jiraConnections.isLoading}
              error={jiraConnections.error}
              isTestingConnection={testJira.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteJira.isPending}
              onTestConnection={handleTestJiraConnection}
              onDeleteConnection={(id) => deleteJira.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('jira')}
              emptyStateText="No Jira connections configured. Add a connection to create issues from findings."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.jira} />}
              customHeaders={
                <>
                  <TableHeaderCell>Deployment</TableHeaderCell>
                  <TableHeaderCell>Auto-Create</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.deploymentType}</TableCell>
                  <TableCell>{connection.autoCreateIssues ? '✓ Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'servicenow':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<ServiceNowConnection & ConnectionItem>
              title="ServiceNow Connections"
              icon={<PlugConnectedRegular />}
              connections={serviceNowConnections.data?.map(c => ({ ...c, instanceUrl: c.instanceUrl })) || []}
              isLoading={serviceNowConnections.isLoading}
              error={serviceNowConnections.error}
              isTestingConnection={testServiceNow.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteServiceNow.isPending}
              onTestConnection={handleTestServiceNowConnection}
              onDeleteConnection={(id) => deleteServiceNow.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('servicenow')}
              emptyStateText="No ServiceNow connections configured. Add a connection to create incidents from findings."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.servicenow} />}
              customHeaders={
                <>
                  <TableHeaderCell>Auto-Create</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.autoCreateIncidents ? '✓ Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'snyk':
        return (
          <div className={styles.section}>
            <IntegrationConnectionsList<SnykConnection & ConnectionItem>
              title="Snyk Connections"
              icon={<ShieldCheckmarkRegular />}
              connections={snykConnections.data?.map(c => ({ ...c, instanceUrl: `app.snyk.io/org/${c.organizationId}` })) || []}
              isLoading={snykConnections.isLoading}
              error={snykConnections.error}
              isTestingConnection={testSnyk.isPending}
              testingConnectionId={testingConnectionId}
              isDeletingConnection={deleteSnyk.isPending}
              onTestConnection={handleTestSnykConnection}
              onDeleteConnection={(id) => deleteSnyk.mutateAsync(id)}
              onAddConnection={() => handleOpenForm('snyk')}
              emptyStateText="No Snyk connections configured. Add a connection to import vulnerabilities from your Snyk organization."
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.snyk} />}
              customHeaders={
                <>
                  <TableHeaderCell>Projects</TableHeaderCell>
                  <TableHeaderCell>Auto-Sync</TableHeaderCell>
                </>
              }
              renderCustomColumns={(connection) => (
                <>
                  <TableCell>{connection.projectCount}</TableCell>
                  <TableCell>{connection.autoSyncVulnerabilities ? '\u2713 Enabled' : 'Disabled'}</TableCell>
                </>
              )}
            />
          </div>
        );

      case 'notifications':
        return (
          <div className={styles.section}>
            <NotificationChannelsList
              onAddChannel={() => handleOpenForm('notifications')}
              emptyStateContent={<IntegrationSetupGuide data={integrationSetupData.notifications} />}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderFormDialog = () => {
    if (!formDialog.isOpen || !formDialog.type) return null;

    let title = '';
    let content: React.ReactNode = null;

    switch (formDialog.type) {
      case 'github':
        title = 'Add GitHub Connection';
        content = (
          <GitHubConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'gitlab':
        title = 'Add GitLab Connection';
        content = (
          <GitLabConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'jira':
        title = 'Add Jira Connection';
        content = (
          <JiraConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'notifications':
        title = 'Add Notification Channel';
        content = (
          <NotificationChannelForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'azure-devops':
        title = 'Add Azure DevOps Connection';
        content = (
          <AzureDevOpsConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'servicenow':
        title = 'Add ServiceNow Connection';
        content = (
          <ServiceNowConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      case 'snyk':
        title = 'Add Snyk Connection';
        content = (
          <SnykConnectionForm
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        );
        break;
      default:
        return null;
    }

    return (
      <Dialog open={formDialog.isOpen} onOpenChange={(_, data) => !data.open && handleCloseForm()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              {content}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text size={600} weight="semibold">
          Integrations
        </Text>
        <Text size={300}>
          Connect LeftShield with your development and operations tools.
        </Text>
      </div>

      {/* Cross-link: Set up CI/CD */}
      {hasRepoConnections && (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>Set up CI/CD pipelines</MessageBarTitle>
            Automate security scanning in your pipelines with GitHub Actions, Azure Pipelines, or GitLab CI.{' '}
            <Button
              appearance="transparent"
              size="small"
              icon={<RocketRegular />}
              iconPosition="after"
              onClick={() => navigate('/developer-tools')}
            >
              Go to Developer Tools
            </Button>
          </MessageBarBody>
        </MessageBar>
      )}

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as TabValue)}
      >
        <Tab value="github" icon={<BranchRegular />}>
          GitHub
        </Tab>
        <Tab value="gitlab" icon={<CodeRegular />}>
          GitLab
        </Tab>
        <Tab value="azure-devops" icon={<CloudRegular />}>
          Azure DevOps
        </Tab>
        <Tab value="jira" icon={<BoardRegular />}>
          Jira
        </Tab>
        <Tab value="servicenow" icon={<PlugConnectedRegular />}>
          ServiceNow
        </Tab>
        <Tab value="snyk" icon={<ShieldCheckmarkRegular />}>
          Snyk
        </Tab>
        <Tab value="notifications" icon={<AlertRegular />}>
          Notifications
        </Tab>
      </TabList>

      <div className={styles.tabContent}>{renderTabContent()}</div>

      {renderFormDialog()}
    </div>
  );
};

export default IntegrationsPage;
